/**
 * Thekedar subscription plans. Prices are in paise and are the full amount the owner pays (see `PLATFORM_GST_REGISTERED`).
 *
 * Paid prices approved by udbhav on 20 Oct 2026 (Solo added 21 Oct 2026). Free is a permanent plan, not a trial.
 *
 * AI is sold as "usage credit", not reply counts (udbhav, 21 Oct 2026). Every call is metered from its real token cost
 * (services/usage.ts). A paid plan may spend 50% of its price as real cost (`planCostCapPaise`, server only); owners see 2x that as
 * usage credit, so a paid plan's monthly credit equals its price. Free may spend 20 rupees of real cost, shown as 40
 * rupees of credit, unlocked by the gate in services/usage.ts. Nobody can cost more than half of what they pay.
 *
 * - WhatsApp/Instagram message fees are charged by Meta to the business's own
 *   Meta account; Razorpay fees to its own Razorpay account. Neither is ours.
 *
 * This file ships to the browser, so it holds only precomputed figures (`aiCreditPaise`, `idealCustomers`) and never imports
 * the real-cost constants in server/lib/ai-pricing.ts; tests/api/plans.test.ts asserts the figures equal the server computation.
 *
 * Feature lists name only what the code provides today.
 */
export const PRICING_APPROVED = true;

/**
 * Whether Thekedar itself is GST-registered. It is not (udbhav, 22 Oct 2026: under the 20 lakh threshold), so its own plan and
 * top-up prices are final and no GST is added; nothing on Thekedar's own pricing, billing, top-up or legal pages may say or imply
 * otherwise. Switch this on only after registering: the copy below then says GST is added. Unrelated to a workspace's own GST
 * settings (`gstRegistered` / `pricesIncludeGst` on its quotes and invoices).
 */
export const PLATFORM_GST_REGISTERED: boolean = false;
/** Suffix after a paid plan's price, e.g. "₹499 / month". */
export const PRICE_PER_MONTH = PLATFORM_GST_REGISTERED ? "/ month + GST" : "/ month";
/** One sentence on tax for Thekedar's own prices. */
export const PLATFORM_PRICE_NOTE = PLATFORM_GST_REGISTERED
  ? "Prices exclude GST, which is added at checkout."
  : "Prices shown are the full amount you pay; no GST is added.";

export type PlanId = "free" | "solo" | "starter" | "growth";

export interface Plan {
  readonly id: PlanId;
  readonly name: string;
  readonly pricePaise: number;
  readonly seats: number;
  /** Monthly usage credit the owner sees, in paise (2x the real-cost cap). */
  readonly aiCreditPaise: number;
  /** "Ideal for up to N customers a month". */
  readonly idealCustomers: number;
  readonly knowledgeDocuments: number;
  readonly features: readonly string[];
}

const rupeesWhole = (paise: number) => `\u20B9${(paise / 100).toLocaleString("en-IN")}`;

/** "₹999 of usage credit a month". */
export const creditLine = (creditPaise: number) => `${rupeesWhole(creditPaise)} of usage credit a month`;
/** "ideal for up to 1,000 customers a month". */
export const customersLine = (customers: number) => `ideal for up to ${customers.toLocaleString("en-IN")} customers a month`;

function make(base: Omit<Plan, "features"> & { extra: readonly string[] }): Plan {
  const { extra, ...rest } = base;
  return { ...rest, features: [...extra, `${creditLine(rest.aiCreditPaise)}, ${customersLine(rest.idealCustomers)}`] };
}

export const plans: readonly Plan[] = [
  make({
    id: "free",
    aiCreditPaise: 4000,
    idealCustomers: 50,
    name: "Free",
    pricePaise: 0,
    seats: 1,
    knowledgeDocuments: 3,
    extra: ["Free forever, no card needed", "Enquiry link, QR and one inbox", "WhatsApp and Instagram, with an AI team", "Payment links via your Razorpay", "Verify your email to start, connect a channel for the full credit"],
  }),
  make({
    id: "solo",
    aiCreditPaise: 49900,
    idealCustomers: 500,
    name: "Solo",
    pricePaise: 49_900,
    seats: 1,
    knowledgeDocuments: 10,
    extra: ["Everything in Free", "More notes for the AI to learn from"],
  }),
  make({
    id: "starter",
    aiCreditPaise: 99900,
    idealCustomers: 1000,
    name: "Starter",
    pricePaise: 99_900,
    seats: 2,
    knowledgeDocuments: 25,
    extra: ["Everything in Solo", "A second seat for your team"],
  }),
  make({
    id: "growth",
    aiCreditPaise: 249900,
    idealCustomers: 2700,
    name: "Growth",
    pricePaise: 249_900,
    seats: 5,
    knowledgeDocuments: 100,
    extra: ["Everything in Starter", "More seats for a growing team"],
  }),
];

export const paidPlans = plans.filter((plan) => plan.pricePaise > 0);

export function planById(id: string): Plan {
  return plans.find((plan) => plan.id === id) ?? plans[0];
}

export function formatRupees(paise: number): string {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(paise / 100);
}
